import { formatTime } from 'shared/helpers/formatTime';
import {
  extractConflictingAllocationId,
  findAllocationById,
} from './eventSlotOverlap';

function localizeBusinessHoursDetail(detail, t) {
  const notOpenDay = detail.match(
    /^day (\d{4}-\d{2}-\d{2}) \((\w+)\) is not an open day$/
  );
  if (notOpenDay) {
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.NOT_OPEN_DAY', {
      date: notOpenDay[1],
      day: notOpenDay[2],
    });
  }

  const startOutside = detail.match(
    /^start time (\d{2}:\d{2}) .+ is outside business hours$/
  );
  if (startOutside) {
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.START_OUTSIDE_HOURS', {
      time: startOutside[1],
    });
  }

  const startAfterReservation = detail.match(
    /^start time (\d{2}:\d{2}) .+ is after reservation end (\d{2}:\d{2})$/
  );
  if (startAfterReservation) {
    return t(
      'PLANNING.ALLOCATIONS.INVALID_REASONS.START_AFTER_RESERVATION_END',
      {
        time: startAfterReservation[1],
        reservationEnd: startAfterReservation[2],
      }
    );
  }

  const endOutside = detail.match(
    /^end time (\d{2}:\d{2}) .+ is outside business hours$/
  );
  if (endOutside) {
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.END_OUTSIDE_HOURS', {
      time: endOutside[1],
    });
  }

  const rangeOutside = detail.match(
    /^time range (\d{2}:\d{2})–(\d{2}:\d{2}) .+ does not fit within a single business hours slot$/
  );
  if (rangeOutside) {
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.RANGE_OUTSIDE_HOURS', {
      start: rangeOutside[1],
      end: rangeOutside[2],
    });
  }

  const closedPeriod = detail.match(
    /^day (\d{4}-\d{2}-\d{2}) falls in (?:recurring )?closed period (.+)$/
  );
  if (closedPeriod) {
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.CLOSED_PERIOD', {
      date: closedPeriod[1],
      period: closedPeriod[2],
    });
  }

  const closedPeriodOverlap = detail.match(
    /^time range (\d{4}-\d{2}-\d{2}T\d{2}:\d{2})–(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}) overlaps (?:recurring )?closed period (.+)$/
  );
  if (closedPeriodOverlap) {
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.CLOSED_PERIOD', {
      date: closedPeriodOverlap[1].slice(0, 10),
      period: closedPeriodOverlap[3],
    });
  }

  return t('PLANNING.ALLOCATIONS.INVALID_REASONS.OUTSIDE_BUSINESS_HOURS');
}

function localizeReasonCode(code, t) {
  const normalized = code.trim();

  if (normalized === 'time_overlap') {
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.TIME_OVERLAP');
  }
  if (normalized === 'hierarchy_or_constraint_collision') {
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.HIERARCHY_COLLISION');
  }
  if (normalized === 'end_at must be after start_at') {
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.END_BEFORE_START');
  }
  if (normalized === 'Validation failed') {
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.VALIDATION_FAILED');
  }
  if (normalized.startsWith('Invalid time granularity:')) {
    if (normalized.includes('5-minute')) {
      return t('PLANNING.ALLOCATIONS.INVALID_REASONS.INVALID_GRANULARITY');
    }
    return t(
      'PLANNING.ALLOCATIONS.INVALID_REASONS.INVALID_GRANULARITY_GENERIC'
    );
  }
  if (normalized.startsWith('outside_business_hours:')) {
    return localizeBusinessHoursDetail(
      normalized.slice('outside_business_hours: '.length),
      t
    );
  }

  return null;
}

export function parseInvalidReason(reason, t) {
  if (!reason) return t('PLANNING.ALLOCATIONS.INVALID_REASONS.GENERIC');

  if (/^Conflicts with allocation [a-f0-9-]+$/i.test(reason)) {
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.CONFLICTS_WITH_ALLOCATION');
  }

  const conflictsDetected = reason.match(
    /^Conflicts detected: (.+?)(?: \(and (\d+) more\))?$/
  );
  if (conflictsDetected) {
    const [, rawReason, moreCount] = conflictsDetected;
    const localized =
      localizeReasonCode(rawReason, t) ||
      t('PLANNING.ALLOCATIONS.INVALID_REASONS.GENERIC');

    if (moreCount) {
      return t('PLANNING.ALLOCATIONS.INVALID_REASONS.AND_MORE', {
        reason: localized,
        count: Number(moreCount),
      });
    }
    return localized;
  }

  return (
    localizeReasonCode(reason, t) ||
    t('PLANNING.ALLOCATIONS.INVALID_REASONS.GENERIC')
  );
}

export function getAllocationStatusLabel(allocation, t) {
  const status = allocation?.status;
  if (!status || status === 'valid') return null;

  if (status === 'confirmed') {
    return t('PLANNING.ALLOCATIONS.STATUSES.CONFIRMED');
  }
  if (status === 'tentative') {
    return t('PLANNING.ALLOCATIONS.STATUSES.TENTATIVE');
  }
  if (status === 'cancelled') {
    return t('PLANNING.ALLOCATIONS.STATUSES.CANCELLED');
  }
  if (status === 'invalid') {
    if (allocation.invalid_reason) {
      return parseInvalidReason(allocation.invalid_reason, t);
    }
    if (allocation.conflicts?.length) {
      const conflict = allocation.conflicts[0];
      const raw = conflict.message || conflict.reason;
      if (raw) return parseInvalidReason(raw, t);
    }
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.GENERIC');
  }

  return status;
}

export function formatConflictLabel(
  target,
  t,
  { getResourceName, currentEventId } = {}
) {
  if (!target) return t('PLANNING.ALLOCATIONS.INVALID_REASONS.TIME_OVERLAP');

  const spaceName = getResourceName?.(target) || '';
  const start = formatTime(target.start_at);
  const end = formatTime(target.end_at);
  const isOtherEvent =
    target.event_id &&
    currentEventId &&
    String(target.event_id) !== String(currentEventId);

  if (isOtherEvent && target.event_name) {
    return t('PLANNING.ALLOCATIONS.INVALID_REASONS.OVERLAPS_WITH_EVENT', {
      event: target.event_name,
      space: spaceName,
      start,
      end,
    });
  }

  return t('PLANNING.ALLOCATIONS.INVALID_REASONS.OVERLAPS_WITH_SLOT', {
    title: target.title || t('PLANNING.SCHEDULER.UNTITLED_SLOT'),
    space: spaceName,
    start,
    end,
  });
}

export function conflictEventUrl(target, currentEventId) {
  const eventId = target?.event_id;
  if (!eventId) return null;
  if (currentEventId && String(eventId) === String(currentEventId)) {
    return null;
  }
  return {
    name: 'planning_events_show',
    params: { eventId },
  };
}

function storeAllocations(store) {
  return store?.getters['planningAllocations/getAllocations'] || [];
}

function withResolvedEventId(target, store) {
  if (!target || target.event_id || !store) return target;

  const parent = findAllocationById(
    storeAllocations(store),
    target.parent_allocation_id
  );
  if (!parent?.event_id) return target;

  return {
    ...target,
    event_id: parent.event_id,
    event_name: target.event_name || parent.event_name,
  };
}

async function lookupConflictById(id, siblings, store) {
  if (!id || !store) return null;
  if (findAllocationById(siblings, id)) return null;

  const fromStore = findAllocationById(storeAllocations(store), id);
  if (fromStore) return withResolvedEventId(fromStore, store);

  try {
    const fetched = await store.dispatch('planningAllocations/get', id);
    return withResolvedEventId(fetched, store);
  } catch {
    return null;
  }
}

export async function resolveConflictTarget({ allocation, siblings, store }) {
  if (allocation?.conflicting_allocation) {
    const peer = withResolvedEventId(allocation.conflicting_allocation, store);
    if (!findAllocationById(siblings, peer?.id)) {
      return peer;
    }
  }

  const storedConflictId = extractConflictingAllocationId(
    allocation?.invalid_reason
  );
  // Same-event siblings are not conflicts.
  if (findAllocationById(siblings, storedConflictId)) return null;

  return lookupConflictById(storedConflictId, siblings, store);
}
